/**
 * Authentication Service – Frontend API client for auth operations
 */

import { getBrowserLanguage } from './api';

// Use relative URL (empty string) so auth requests use the same domain as the app
// This works for both local development (if backend runs on same port) and production
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

// =====================================
// Token Management
// =====================================

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

export function setTokens(tokens: AuthTokens): void {
  localStorage.setItem('accessToken', tokens.accessToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

export function getStoredUser(): User | null {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
}

export function setStoredUser(user: User): void {
  localStorage.setItem('user', JSON.stringify(user));
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// =====================================
// API Helpers
// =====================================

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {},
  _isRetry = false
): Promise<any> {
  const token = getAccessToken();
  
  // Don't set Content-Type for FormData – the browser sets it automatically with the boundary
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {}
    : { 'Content-Type': 'application/json' };

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // If 401 and we haven't retried yet, try to refresh the token
  if (response.status === 401 && !_isRetry && getRefreshToken()) {
    try {
      await refreshAccessToken();
      // Retry the original request with the new token
      return apiRequest(endpoint, options, true);
    } catch (refreshErr) {
      // Refresh failed - clear tokens and redirect to login
      clearTokens();
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || 'Request failed');
  }

  return data;
}

// =====================================
// Auth API Methods
// =====================================

export async function register(data: RegisterData): Promise<{ user: User; message: string }> {
  const response = await apiRequest('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  return response.data;
}

export async function login(data: LoginData): Promise<{ user: User; tokens: AuthTokens }> {
  const response = await apiRequest('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  // Store tokens and user
  setTokens({
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
  });
  setStoredUser(response.data.user);

  return response.data;
}

export async function verifyEmail(token: string): Promise<{ user: User; message: string }> {
  const response = await apiRequest(`/api/auth/verify-email?token=${token}`, {
    method: 'GET',
  });

  return response.data;
}

export async function refreshAccessToken(): Promise<AuthTokens> {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await apiRequest('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });

  setTokens(response.data);
  return response.data;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  
  if (refreshToken) {
    try {
      await apiRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch (err) {
      // Continue even if logout fails
    }
  }

  clearTokens();
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiRequest('/api/auth/me', {
    method: 'GET',
  });

  setStoredUser(response.data.user);
  return response.data.user;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  const response = await apiRequest('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return response.data;
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await apiRequest('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return response.data;
}

export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const response = await apiRequest('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
  return response.data;
}

/**
 * Client-side password validation matching server rules:
 * min 8 chars, 1 uppercase, 1 special character
 * Returns null if valid, or error message string.
 */
export function validatePassword(password: string): string | null {
  if (!password || password.length < 8) {
    return 'Passwort muss mindestens 8 Zeichen lang sein';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Passwort muss mindestens einen Grossbuchstaben enthalten';
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    return 'Passwort muss mindestens ein Sonderzeichen enthalten';
  }
  return null;
}

// =====================================
// Search History API
// =====================================

export async function getSearchHistory(): Promise<any[]> {
  const response = await apiRequest('/api/search-history', { method: 'GET' });
  return response.data.searches;
}

export async function rerunSearch(searchId: number): Promise<any> {
  const response = await apiRequest(`/api/search-history/${searchId}/rerun`, {
    method: 'POST',
  });
  return response.data;
}

export async function deleteSearchHistory(searchId: number): Promise<void> {
  await apiRequest(`/api/search-history/${searchId}`, { method: 'DELETE' });
}

export async function deleteAllSearchHistory(): Promise<any> {
  return apiRequest('/api/search-history/all', { method: 'DELETE' });
}

export async function saveSearch(data: {
  profileName: string;
  chatMessages: any[];
  profile: any;
  results: any[];
  aiProvider: string;
  lifestyleEnabled?: boolean;
}): Promise<any> {
  const response = await apiRequest('/api/search-history', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
}

// =====================================
// Favorites API
// =====================================

export async function getFavorites(): Promise<any[]> {
  const response = await apiRequest('/api/favorites', { method: 'GET' });
  return response.data.favorites;
}

export async function addFavorite(propertyId: number, searchQueryId?: number): Promise<any> {
  const response = await apiRequest('/api/favorites', {
    method: 'POST',
    body: JSON.stringify({ propertyId, searchQueryId }),
  });
  return response.data;
}

export async function removeFavorite(propertyId: number): Promise<void> {
  await apiRequest(`/api/favorites/${propertyId}`, { method: 'DELETE' });
}

export async function checkFavorite(propertyId: number): Promise<boolean> {
  const response = await apiRequest(`/api/favorites/check/${propertyId}`, { method: 'GET' });
  return response.data.isFavorite;
}

// =====================================
// Lifestyle Profile API
// =====================================

export async function getLifestyleProfile(): Promise<{
  profile: any | null;
  summary: string | null;
  updatedAt: string | null;
}> {
  const response = await apiRequest('/api/lifestyle-profile', { method: 'GET' });
  return response.data;
}

export async function updateLifestyleProfile(profile: any): Promise<{
  profile: any;
  summary: string | null;
  message: string;
}> {
  const response = await apiRequest('/api/lifestyle-profile', {
    method: 'PUT',
    body: JSON.stringify({ profile, language: getBrowserLanguage() }),
  });
  return response.data;
}

export async function synthesizeLifestyleProfile(): Promise<{
  profile: any;
  summary: string;
  searchCount: number;
}> {
  const response = await apiRequest('/api/lifestyle-profile/synthesize', {
    method: 'POST',
    body: JSON.stringify({ language: getBrowserLanguage() }),
  });
  return response.data;
}

export async function resetLifestyleProfile(): Promise<{ message: string }> {
  const response = await apiRequest('/api/lifestyle-profile', { method: 'DELETE' });
  return response.data;
}

// =====================================
// Comparisons API
// =====================================

export async function generateComparison(data: {
  listings: any[];
  profile: any;
  lifestyleProfile?: any;
  provider?: string;
}): Promise<{
  id: number;
  comparison: string;
  provider: string;
  propertyIds: number[];
  propertyTitles: string[];
}> {
  const response = await apiRequest('/api/comparisons/generate', {
    method: 'POST',
    body: JSON.stringify({ ...data, language: getBrowserLanguage() }),
  });
  return response.data;
}

export async function getComparisons(): Promise<any[]> {
  const response = await apiRequest('/api/comparisons', { method: 'GET' });
  return response.data.comparisons;
}

export async function deleteComparison(comparisonId: number): Promise<void> {
  await apiRequest(`/api/comparisons/${comparisonId}`, { method: 'DELETE' });
}

// =====================================
// Valuations API (Pillar 2: Rate)
// =====================================

export async function analyzeProperty(data: {
  files?: File[];
  url?: string;
  provider?: string;
}): Promise<{
  id: number;
  extracted: any;
  valuation: string;
  shortConclusion?: string;
  comparables: any[];
  priceEstimate: any;
  provider: string;
}> {
  const formData = new FormData();
  if (data.files && data.files.length > 0) {
    data.files.forEach(f => formData.append('files', f));
  }
  if (data.url) formData.append('url', data.url);
  if (data.provider) formData.append('provider', data.provider);
  formData.append('language', getBrowserLanguage());

  const token = getAccessToken();

  // Long timeout for AI pipeline (extraction → web search → valuation report)
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 180000); // 3 minutes

  try {
    const res = await fetch('/api/valuations/analyze', {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
      signal: controller.signal,
    });
    clearTimeout(timeout);
    const json = await res.json();
    if (!json.success) {
      const err = new Error(json.error?.message || 'Analyse fehlgeschlagen') as any;
      err.errorType = json.error?.type || 'unknown';
      throw err;
    }
    return json.data;
  } catch (err: any) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      throw new Error('Die Analyse hat zu lange gedauert. Bitte versuchen Sie es erneut.');
    }
    throw err;
  }
}

export async function getValuations(): Promise<any[]> {
  const response = await apiRequest('/api/valuations', { method: 'GET' });
  return response.data.valuations;
}

export async function deleteValuation(valuationId: number): Promise<void> {
  await apiRequest(`/api/valuations/${valuationId}`, { method: 'DELETE' });
}

export async function deleteAllValuations(): Promise<void> {
  await apiRequest('/api/valuations/all', { method: 'DELETE' });
}

// =====================================
// Lifestyle Toggle (persisted in localStorage)
// =====================================

const LIFESTYLE_TOGGLE_KEY = 'lifestyleToggleEnabled';

export function getLifestyleToggle(): boolean {
  const stored = localStorage.getItem(LIFESTYLE_TOGGLE_KEY);
  // Default to true (enabled) if not set
  return stored === null ? true : stored === 'true';
}

export function setLifestyleToggle(enabled: boolean): void {
  localStorage.setItem(LIFESTYLE_TOGGLE_KEY, String(enabled));
}

// =====================================
// Auto-refresh logic (optional but recommended)
// =====================================

let refreshTimer: ReturnType<typeof setInterval> | null = null;

export function startTokenRefresh(): void {
  // Refresh token every 10 minutes (access token expires in 15 min)
  refreshTimer = setInterval(async () => {
    if (isAuthenticated()) {
      try {
        await refreshAccessToken();
        console.log('[Auth] Token refreshed successfully');
      } catch (err) {
        console.error('[Auth] Token refresh failed:', err);
        clearTokens();
        window.location.href = '/login';
      }
    }
  }, 10 * 60 * 1000); // 10 minutes
}

export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}
